import {
    HttpForbiddenError,
    HttpNotFoundError,
    HttpRequestFailedError,
    HttpUnauthorizedError,
} from './httpClient';

/**
 * A failed request as a sentence somebody can act on.
 *
 * The raw `Error.message` for a request failure reads "Request to /api/check-ins returned 404" — a
 * sentence about a URL, addressed to whoever wrote the code. The reader wants to know whether to retry,
 * ask for access, or report it.
 *
 * The distinctions come from httpClient's typed errors, which exist for exactly this. Where the API sent a
 * reason of its own it is used verbatim: 401 and 403 both carry one, and the server's explanation of why an
 * identity was refused is always better than anything guessable from the status alone.
 */
export const describeError = (error: unknown): string => {
    if (error instanceof HttpNotFoundError) {
        return 'It no longer exists, or it was never there. If you followed a link, whatever it pointed at has gone.';
    }

    if (error instanceof HttpUnauthorizedError) {
        return error.detail ?? 'Your identity could not be verified. Reloading signs you in again.';
    }

    if (error instanceof HttpForbiddenError) {
        return (
            error.detail ?? 'Your account is not allowed to do this. A site admin can change that.'
        );
    }

    /*
     * Everything else non-OK. The server's own words pass through when it sent any; without them, the
     * fallback message inside the error names the URL and status — exactly what this helper exists to keep
     * off screens — so the generic case gets a sentence about what to do instead.
     */
    if (error instanceof HttpRequestFailedError) {
        return (
            error.detail ??
            'The server hit a problem handling this. Trying again usually works; if it keeps failing, it is worth reporting.'
        );
    }

    /*
     * `fetch` rejects with a TypeError for anything that stopped the request leaving — offline, DNS, a
     * connection dropped mid-flight. It is the one failure a reader can often fix themselves, and the raw
     * message ("Failed to fetch") says nothing about how.
     */
    if (error instanceof TypeError) {
        return 'The server could not be reached. Check your connection, then try again.';
    }

    // A plain Error that reaches here carries a message somebody composed, not a request trace — every
    // HTTP shape has a typed class above. Worth passing through.
    if (error instanceof Error && error.message.trim() !== '') {
        return error.message;
    }

    return 'Something went wrong, and the cause was not reported. Trying again is worth a go before reporting it.';
};
